import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';

const LocationAccuracyChart = ({ locationAccuracy, handleDrillDown }) => {
  const chartRef = useRef(null);
  
  if (!locationAccuracy || locationAccuracy.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-6xl mb-4 text-gray-300">🏢</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Location Accuracy</h3>
        <p className="text-gray-600">No data available</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  const sortedData = [...locationAccuracy].sort((a, b) => a.accuracy - b.accuracy);
  // console.log('Location Accuracy Chart Data:', sortedData);
  
  const chartData = {
    labels: sortedData.map(item => item.locationName),
    datasets: [
      {
        label: 'Accuracy %',
        data: sortedData.map(item => item.accuracy),
        backgroundColor: '#059669',
        borderColor: '#059669',
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      },
    ],
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, activeElements) => {
      if (event?.native) {
        event.native.target.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
      }
    },
    interaction: {
      intersect: false
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: '#f3f4f6',
          borderColor: '#e5e7eb'
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
            family: 'system-ui'
          }
        },
        title: {
          display: true,
          text: 'Accuracy (%)',
          color: '#374151',
          font: {
            size: 13,
            weight: '600',
            family: 'system-ui'
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          color: '#6b7280',
          font: {
            size: 11,
            family: 'system-ui'
          }
        }
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#ffffff',
        bodyColor: '#e5e7eb',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return [
              `Accuracy: ${context.parsed.y}%`,
              'Click to drill down'
            ];
          }
        }
      },
      legend: {
        display: false
      }
    }
  };
  
  return (
    <div className="bg-white p-6">
      <div className="mb-6">
        <div className="flex items-center justify-end">
          <span className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200">
            {sortedData.length} Locations
          </span>
        </div>
      </div>
      
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="h-[400px]">
          <Bar 
            ref={chartRef}
            data={chartData} 
            options={options} 
            onClick={(event) => {
              const chart = chartRef.current;
              if (chart) {
                const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
                if (elements.length > 0) {
                  const dataIndex = elements[0].index;
                  const locationData = sortedData[dataIndex];
                  handleDrillDown('location', locationData);
                }
              }
            }}
          />
        </div>
      </div>
      
      {/* Quick stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(l => l.accuracy >= 95).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Excellent (&ge;95%)</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(l => l.accuracy >= 85 && l.accuracy < 95).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Good (85-94%)</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(l => l.accuracy >= 70 && l.accuracy < 85).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Average (70-84%)</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(l => l.accuracy < 70).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Needs Attention (&lt;70%)</div>
        </div>
      </div>
    </div>
  );
};

export default LocationAccuracyChart;